import React from 'react';
import { AlertCircle } from 'lucide-react';
import { StarterManagementStrategy, MANAGEMENT_STRATEGIES, StarterStorage } from '../../types/baking';

interface ManagementStrategySelectorProps {
  value: StarterManagementStrategy;
  storage: StarterStorage;
  onChange: (strategy: StarterManagementStrategy) => void;
}

export function ManagementStrategySelector({ 
  value, 
  storage,
  onChange 
}: ManagementStrategySelectorProps) {
  const formatInterval = (hours: number) => {
    if (hours < 24) return `${hours} hours`;
    const days = hours / 24;
    return `${days} day${days > 1 ? 's' : ''}`;
  };

  return (
    <div className="space-y-4">
      {Object.values(MANAGEMENT_STRATEGIES).map((strategy) => {
        const isSelected = value === strategy.id;
        const isDisabled = storage === 'fridge' && !strategy.allowFridge;
        
        return (
          <label
            key={strategy.id}
            className={`block relative p-4 border rounded-xl transition-all duration-200 ${
              isDisabled
                ? 'opacity-50 cursor-not-allowed border-white/10 bg-white/5'
                : isSelected
                ? 'border-amber-400/50 bg-gradient-to-b from-amber-400/20 to-amber-500/10 cursor-pointer'
                : 'border-white/10 bg-white/5 hover:bg-white/10 cursor-pointer'
            }`}
          >
            <input
              type="radio"
              name="management-strategy"
              value={strategy.id}
              checked={isSelected}
              onChange={() => !isDisabled && onChange(strategy.id as StarterManagementStrategy)}
              className="sr-only"
              disabled={isDisabled}
            />

            <div className="flex justify-between items-start mb-2">
              <div>
                <h3 className="font-medium text-white">{strategy.label}</h3>
                <p className="text-sm text-white/70 mt-1">{strategy.description}</p>
              </div>
            </div>

            <div className="mt-3 space-y-2 text-sm text-white/80">
              <p>Room temperature: Feed every {formatInterval(strategy.feedingInterval.room)}</p>
              {strategy.allowFridge ? (
                <p>Refrigerated: Feed every {formatInterval(strategy.feedingInterval.fridge)}</p>
              ) : (
                <div className="flex items-center gap-2 text-amber-200">
                  <AlertCircle className="w-4 h-4" />
                  <p>Refrigeration not recommended</p>
                </div>
              )}
            </div>

            {isSelected && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-400 rounded-full border-2 border-black/20" />
            )}
          </label>
        );
      })}
    </div>
  );
}